import MoreVertIcon from '@mui/icons-material/MoreVert';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  Modal,
  SxProps,
  Typography,
} from '@mui/material';
import { AxiosError } from 'axios';
import { t } from 'i18next';
import { useEffect, useRef, useState } from 'react';
import { Trans } from 'react-i18next';
import ColorConstant from '../../constants/ColorConstant';
import { ProviderEnum } from '../../constants/ProviderEnum';
import { IBAN_UPDATE_WORKFLOW_PLACES } from '../../constants/iban-update-workflow-places';
import IDocumentError from '../../interfaces/IDocumentError';
import { IIbanUpdateWithDocumentError } from '../../interfaces/IIbanUpdate';
import IUser from '../../interfaces/IUser';
import IbanUpdateService from '../../services/IbanUpdateService';
import SnackbarService from '../../services/SnackbarService';

const styles: { [key: string]: SxProps } = {
  modalWrapper: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '30%',
    minWidth: '500px',
    bgcolor: 'background.paper',
    boxShadow: 24,
    maxHeight: '90vh',
    overflow: 'auto',
  },
  modalHeaderWrapper: {
    display: 'flex',
    alignItems: 'center',
    borderBottom: `1px solid ${ ColorConstant.lightGray }`,
  },
  modalHeader: {
    flex: 1,
    display: 'flex',
    justifyContent: 'space-between',
    padding: '16px 32px',
  },
  buttonWrapper: {
    display: 'flex',
    justifyContent: 'flex-end',
    p: 2,
  },
  diplayInCenter: {
    display: 'flex',
    justifyContent: 'center',
    fontSize: '14px',
    p: 2,
  },
  msg: {
    padding: '8px 32px',
    fontSize: '.8rem',
  },
  errorMsg: {
    padding: '8px 32px',
    fontSize: '.8rem',
    color: ColorConstant.theme.palette.warning.main,
  },
};

export default function IbanUpdateDetailsModal({
  isIbanUpdateDetailsModalOpen,
  ibanUpdate,
  investmentAccounts,
  handleCloseModal,
}: IIbanUpdateDetailsModalProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [resumeBtnIsLoading, setResumeBtnIsLoading] = useState(false);
  const [deleteBtnIsLoading, setDeleteBtnIsLoading] = useState(false);

  const abortControllerRef = useRef(new AbortController());
  const ibanUpdateService = new IbanUpdateService(abortControllerRef.current.signal);

  useEffect(() => {
    return () => {
      abortControllerRef.current.abort();
    };
  }, []);

  const handleIsMenuOpen = (opening: boolean, event?: React.MouseEvent<HTMLElement>) => {
    setIsMenuOpen(opening);
    setAnchorEl(opening ? event?.currentTarget ?? null : null);
  };

  const retryRequest = async () => {
    setResumeBtnIsLoading(true);
    try {
      await ibanUpdateService.retryRequest(ibanUpdate['@id']);
      SnackbarService.open(t('success_resume_operation'), 'info');
    } catch (error) {
      SnackbarService.open(t('failed_resume_operation'), 'error');
      console.error(error);
    } finally {
      setResumeBtnIsLoading(false);
      setIsMenuOpen(false);
    }
  };

  const deleteRequest = async () => {
    setDeleteBtnIsLoading(true);
    try {
      await ibanUpdateService.cancelRequest(ibanUpdate['@id']);
      SnackbarService.open(t('success_delete_operation'), 'success');
      await handleCloseModal(true);
    } catch (error) {
      const errorMsg = `${ t('failed_delete_operation') } : ${
        (error as AxiosError<{ 'hydra:description': string }>).response?.data['hydra:description']
      }`;

      SnackbarService.open(errorMsg, 'error');
      console.error(error);
    } finally {
      setDeleteBtnIsLoading(false);
      setIsMenuOpen(false);
    }
  };

  const provider = investmentAccounts?.[0].provider;

  const isResumeItemDisabled =
    provider === ProviderEnum.APICIL ||
    ibanUpdate.place !== IBAN_UPDATE_WORKFLOW_PLACES.PROCESSING ||
    ibanUpdate.automatic ||
    !ibanUpdate.errors;

  const isDeleteItemDisabled = ibanUpdate.place !== IBAN_UPDATE_WORKFLOW_PLACES.PROCESSING;

  const normalizedErrors: IDocumentError[] = Array.isArray(ibanUpdate.errors)
    ? ibanUpdate.errors
    : ibanUpdate.errors
      ? Object.entries(ibanUpdate.errors).map(([key, detail]) => ({ key, detail }))
      : [];

  return (
    <>
      <Modal open={isIbanUpdateDetailsModalOpen} onClose={() => handleCloseModal()}>
        <Card sx={styles.modalWrapper}>
          <Box sx={styles.modalHeaderWrapper}>
            <Box sx={styles.modalHeader}>
              <Typography variant="h5">
                <Trans>operation_iban_update</Trans>
              </Typography>
            </Box>

            <IconButton onClick={(evt) => handleIsMenuOpen(true, evt)}>
              <MoreVertIcon fontSize="large" />
            </IconButton>
          </Box>

          <CardContent sx={{ p: 0 }}>
            { ibanUpdate.errors && (
              <Typography sx={styles.errorMsg}>
                <Trans>error</Trans> :
                { normalizedErrors.map(({ detail }) => {
                  return (
                    <>
                      <br />
                      - { detail }
                    </>
                  );
                }) }
              </Typography>
            ) }
            <Typography sx={styles.msg}>
              <Trans>iban</Trans> : { ibanUpdate.iban } <br />
              <Trans>bic</Trans> : { ibanUpdate.bic }
            </Typography>
          </CardContent>

          <Box sx={styles.buttonWrapper}>
            <Button color="secondary" variant="contained" onClick={() => handleCloseModal()}>
              <Trans>close_button</Trans>
            </Button>
          </Box>
        </Card>
      </Modal>

      <Menu anchorEl={anchorEl} open={isMenuOpen} onClose={() => handleIsMenuOpen(false)}>
        <MenuItem
          onClick={retryRequest}
          disabled={isResumeItemDisabled || resumeBtnIsLoading}
          sx={styles.diplayInCenter}
        >
          { resumeBtnIsLoading ? <CircularProgress size={24} /> : <Trans>resume</Trans> }
        </MenuItem>
        <MenuItem
          onClick={deleteRequest}
          disabled={isDeleteItemDisabled || deleteBtnIsLoading}
          sx={styles.diplayInCenter}
        >
          { deleteBtnIsLoading ? <CircularProgress size={24} /> : <Trans>delete</Trans> }
        </MenuItem>
      </Menu>
    </>
  );
}

interface IIbanUpdateDetailsModalProps {
  isIbanUpdateDetailsModalOpen: boolean;
  ibanUpdate: IIbanUpdateWithDocumentError;
  investmentAccounts: IUser['investmentAccounts'];
  handleCloseModal(hasChangedValue?: boolean): Promise<void>;
}
